/**
 * Mbp wire events. Session/user/pageview lifecycle events are owned by the
 * kernel (`core/analytics/coreEvents.ts`) and are NOT re-emitted here.
 * Traffic is separated in BI by the website's activity model.
 *
 * The `bf_*` types are specific to this module (RND-11709) and count backfill
 * fills; `bf_a9_*` measure whether A9 exposed a usable price on those fills, so
 * the cost of full A9 support can be judged before building it. Exactly one
 * `bf_a9_*` fires per backfill fill — "A9 was absent" is counted, not subtracted,
 * so a dropped event cannot masquerade as A9 having no bid.
 */

export const MBP_EVENTS = {
  /** GPT `slotRequested` (consumer AD_IMPRESSIONS). */
  adRequest: 'ad_imp',
  /** GPT `impressionViewable` (consumer VIEWABLE_IMPRESSIONS). */
  adView: 'adview',
  /** Backfill fill that outbid a Prebid bid (`hb_pb` > 0 on the slot). */
  backfillBeatHb: 'bf_beat_hb',
  /** Backfill fill with no Prebid bid to beat (no `hb_pb` on the slot). */
  backfillNoHb: 'bf_no_hb',
  /** Backfill fill where A9 exposed a priced `amznbid` code. */
  backfillA9Priced: 'bf_a9_priced',
  /** Backfill fill where A9 exposed only a no-rate code (`0`/`1`/`2`). */
  backfillA9Unpriced: 'bf_a9_unpriced',
  /** Backfill fill where A9 left no `amznbid` at all by render time. */
  backfillA9None: 'bf_a9_none',
  /** Voltax player ready (Minute Media / STN). */
  videoLoad: 'video:load',
  /** Voltax ad impression with CPM (Minute Media / STN). */
  videoImpression: 'video:impression',
  /** Voltax player or ad error. */
  videoError: 'video:error',
} as const;

/**
 * analytics-consumer `PredictionRoute` groups. HEADER_BIDDING and DIRECT_DEAL
 * revenue is the client `bid` verbatim; ADX / ADSENSE / EBDA share one
 * server-side predictor that derives revenue from `bid`.
 *
 * DIRECT_DEAL is never emitted here — it marks inventory we sold ourselves,
 * which a passive observer can't recognise.
 */
export const enum RevenueGroup {
  NO_FILL = 0,
  ADX = 1,
  HEADER_BIDDING = 2,
  ADSENSE = 3,
  EBDA = 4,
  DIRECT_DEAL = 5,
}
